import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Trash2, Search, Loader2, AlertTriangle, X, RefreshCw,
    CheckSquare, Square, ChevronLeft, ChevronRight, Download, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

/**
 * V28 — Reusable Bulk Delete Panel.
 *
 * Props:
 *   moduleKey:  e.g. "inquiries" | "registrations" | "tutor-applications"
 *   meta:       module metadata from /admin/bulk-delete/modules
 *               { key, label, columns, status_values, status_field, date_field, search_fields }
 *
 * Add a new module by:
 *   1. Adding it to /app/backend/bulk_delete.py MODULE_REGISTRY.
 *   2. Adding a tab entry pointing this component at the new moduleKey.
 * The panel itself doesn't change.
 */
export default function BulkDeletePanel({ moduleKey, meta }) {
    const [items, setItems] = useState([]);
    const [allFilteredIds, setAllFilteredIds] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Filters
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");

    // Selection (Set of record IDs)
    const [selected, setSelected] = useState(() => new Set());

    // Delete confirmation dialog
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmMode, setConfirmMode] = useState("selected"); // selected | filtered
    const [deleting, setDeleting] = useState(false);
    const [filterPhrase, setFilterPhrase] = useState("");

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const fetchPage = useCallback(async (opts = {}) => {
        const params = {
            page: opts.page ?? page,
            page_size: pageSize,
            ...(appliedSearch ? { search: appliedSearch } : {}),
            ...(status && status !== "all" ? { status } : {}),
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
        };
        try {
            opts.silent ? setRefreshing(true) : setLoading(true);
            const res = await api.get(`/admin/bulk-delete/${moduleKey}`, { params });
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
            setAllFilteredIds(res.data.all_filtered_ids || []);
            if (opts.page) setPage(opts.page);
        } catch (err) {
            toast.error("Failed to load records", {
                description: formatApiError(err.response?.data?.detail) || err.message,
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [moduleKey, page, pageSize, appliedSearch, status, dateFrom, dateTo]);

    // Reload on module / page / filter change
    useEffect(() => {
        setSelected(new Set());
        fetchPage({ page: 1 });
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleKey, appliedSearch, status, dateFrom, dateTo, pageSize]);

    useEffect(() => {
        fetchPage({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // ---------- Selection helpers ----------
    const isSelected = (id) => selected.has(id);
    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const pageIds = items.map((i) => i.id).filter(Boolean);
    const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    const someOnPageSelected = pageIds.some((id) => selected.has(id));
    const togglePage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
            else pageIds.forEach((id) => next.add(id));
            return next;
        });
    };
    const selectAllFiltered = () => setSelected(new Set(allFilteredIds));
    const clearSelection = () => setSelected(new Set());

    // ---------- Delete ----------
    const openConfirm = (mode) => {
        setConfirmMode(mode);
        setFilterPhrase("");
        setConfirmOpen(true);
    };
    const performDelete = async () => {
        setDeleting(true);
        try {
            if (confirmMode === "selected") {
                const ids = Array.from(selected);
                if (!ids.length) return;
                const res = await api.post(`/admin/bulk-delete/${moduleKey}/delete`, { ids });
                toast.success("Records deleted", {
                    description: `Selected records have been deleted successfully and storage space has been released. (${res.data.deleted} of ${res.data.requested})`,
                });
            } else {
                const payload = {
                    confirm_phrase: "DELETE ALL FILTERED",
                    ...(appliedSearch ? { search: appliedSearch } : {}),
                    ...(status && status !== "all" ? { status } : {}),
                    ...(dateFrom ? { date_from: dateFrom } : {}),
                    ...(dateTo ? { date_to: dateTo } : {}),
                };
                const res = await api.post(`/admin/bulk-delete/${moduleKey}/delete-filtered`, payload);
                toast.success("Records deleted", {
                    description: `Selected records have been deleted successfully and storage space has been released. (${res.data.deleted} of ${res.data.matched})`,
                });
            }
            setConfirmOpen(false);
            setSelected(new Set());
            await fetchPage({ page: 1, silent: true });
            setPage(1);
        } catch (err) {
            toast.error("Delete failed", {
                description: formatApiError(err.response?.data?.detail) || err.message,
            });
        } finally {
            setDeleting(false);
        }
    };

    // ---------- Export selected (CSV) ----------
    const exportSelectedCsv = () => {
        const ids = Array.from(selected);
        const rows = items.filter((it) => ids.includes(it.id));
        if (!rows.length) {
            toast.message("Open the matching pages first to export — only items currently loaded can be exported.");
            return;
        }
        const headers = meta.columns.map((c) => c.label).concat(["id"]);
        const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const csv = [
            headers.join(","),
            ...rows.map((r) => meta.columns.map((c) => escape(r[c.key])).concat([escape(r.id)]).join(",")),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${moduleKey}-${new Date().toISOString().slice(0, 10)}-selected.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${rows.length} records`);
    };

    return (
        <div className="space-y-4" data-testid={`bulk-delete-panel-${moduleKey}`}>
            {/* ---- Filter bar ---- */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-4 h-4 text-[#1E3A8A]" />
                    <div className="font-semibold text-sm text-slate-800">Filters</div>
                    <div className="text-xs text-slate-400 ml-auto">{total} total · {selected.size} selected</div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    <FilterField label="Search (name / email / mobile)">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && setAppliedSearch(search.trim())}
                                placeholder="Type and press Enter…"
                                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15 focus:border-[#1E3A8A]"
                                data-testid={`bulk-delete-${moduleKey}-search-input`}
                            />
                        </div>
                    </FilterField>
                    <FilterField label="Status">
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
                            data-testid={`bulk-delete-${moduleKey}-status-select`}
                        >
                            <option value="all">All statuses</option>
                            {(meta.status_values || []).map((s) => (
                                <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
                            ))}
                        </select>
                    </FilterField>
                    <FilterField label="From date">
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm rounded-lg border border-slate-300"
                            data-testid={`bulk-delete-${moduleKey}-date-from`}
                        />
                    </FilterField>
                    <FilterField label="To date">
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm rounded-lg border border-slate-300"
                            data-testid={`bulk-delete-${moduleKey}-date-to`}
                        />
                    </FilterField>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <button
                        onClick={() => setAppliedSearch(search.trim())}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#1E3A8A] text-white hover:bg-[#1E40AF] inline-flex items-center gap-1.5"
                        data-testid={`bulk-delete-${moduleKey}-apply`}
                    >
                        <Search className="w-3.5 h-3.5" /> Apply
                    </button>
                    <button
                        onClick={() => {
                            setSearch(""); setAppliedSearch(""); setStatus("all"); setDateFrom(""); setDateTo("");
                        }}
                        className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5"
                        data-testid={`bulk-delete-${moduleKey}-reset`}
                    >
                        <X className="w-3.5 h-3.5" /> Reset
                    </button>
                    <button
                        onClick={() => fetchPage({ silent: true })}
                        className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5"
                        data-testid={`bulk-delete-${moduleKey}-refresh`}
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
            </div>

            {/* ---- Bulk action bar ---- */}
            {selected.size > 0 && (
                <div className="sticky top-2 z-10 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-rose-50 border border-violet-200 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2 flex-wrap animate-in fade-in duration-150" data-testid={`bulk-delete-${moduleKey}-action-bar`}>
                    <span className="font-bold text-violet-900 text-sm">
                        <span data-testid={`bulk-delete-${moduleKey}-selected-count`}>{selected.size}</span> record{selected.size === 1 ? "" : "s"} selected
                    </span>
                    <span className="text-xs text-slate-500">·</span>
                    <button onClick={selectAllFiltered}
                        className="text-xs underline text-violet-800 hover:text-violet-900"
                        data-testid={`bulk-delete-${moduleKey}-select-all-filtered`}>
                        Select all {total} filtered
                    </button>
                    <button onClick={clearSelection} className="text-xs underline text-slate-600 hover:text-slate-800" data-testid={`bulk-delete-${moduleKey}-clear`}>
                        Clear selection
                    </button>
                    <div className="ml-auto inline-flex items-center gap-2">
                        <button
                            onClick={exportSelectedCsv}
                            className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5"
                            data-testid={`bulk-delete-${moduleKey}-export`}
                        >
                            <Download className="w-3.5 h-3.5" /> Export CSV
                        </button>
                        <button
                            onClick={() => openConfirm("selected")}
                            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1.5"
                            data-testid={`bulk-delete-${moduleKey}-delete-selected`}
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Delete Selected
                        </button>
                    </div>
                </div>
            )}

            {/* ---- Table ---- */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                    <table className="w-full text-sm" data-testid={`bulk-delete-${moduleKey}-table`}>
                        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-[1]">
                            <tr>
                                <th className="px-3 py-2 text-left w-[44px]">
                                    <button
                                        onClick={togglePage}
                                        title={allOnPageSelected ? "Unselect page" : "Select page"}
                                        data-testid={`bulk-delete-${moduleKey}-select-page`}
                                    >
                                        {allOnPageSelected ? (
                                            <CheckSquare className="w-4 h-4 text-[#1E3A8A]" />
                                        ) : someOnPageSelected ? (
                                            <CheckSquare className="w-4 h-4 text-slate-400" />
                                        ) : (
                                            <Square className="w-4 h-4 text-slate-400" />
                                        )}
                                    </button>
                                </th>
                                {meta.columns.map((c) => (
                                    <th key={c.key} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-slate-600">
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={meta.columns.length + 1} className="text-center py-10 text-slate-400">
                                    <Loader2 className="w-5 h-5 inline animate-spin" /> Loading…
                                </td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={meta.columns.length + 1} className="text-center py-10 text-slate-400 italic">
                                    No records match the current filters.
                                </td></tr>
                            ) : items.map((it) => (
                                <tr
                                    key={it.id}
                                    className={`border-b border-slate-100 hover:bg-slate-50 transition ${isSelected(it.id) ? "bg-violet-50/50" : ""}`}
                                    data-testid={`bulk-delete-${moduleKey}-row-${it.id}`}
                                >
                                    <td className="px-3 py-2">
                                        <button
                                            onClick={() => toggle(it.id)}
                                            title={isSelected(it.id) ? "Unselect" : "Select"}
                                            data-testid={`bulk-delete-${moduleKey}-checkbox-${it.id}`}
                                        >
                                            {isSelected(it.id)
                                                ? <CheckSquare className="w-4 h-4 text-[#1E3A8A]" />
                                                : <Square className="w-4 h-4 text-slate-400" />}
                                        </button>
                                    </td>
                                    {meta.columns.map((c) => (
                                        <td key={c.key} className="px-3 py-2 text-slate-700 align-top">
                                            <Cell value={it[c.key]} type={c.type} statusValues={meta.status_values} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination + delete-filtered footer */}
                <div className="flex items-center gap-2 flex-wrap justify-between px-3 py-2 border-t border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1 || loading}
                            className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-white disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`bulk-delete-${moduleKey}-prev`}
                        >
                            <ChevronLeft className="w-3 h-3" /> Prev
                        </button>
                        <span className="text-xs text-slate-600 px-2">Page <strong>{page}</strong> / {totalPages}</span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages || loading}
                            className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-white disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`bulk-delete-${moduleKey}-next`}
                        >
                            Next <ChevronRight className="w-3 h-3" />
                        </button>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                            className="ml-2 px-2 py-1 text-xs rounded border border-slate-300"
                            data-testid={`bulk-delete-${moduleKey}-page-size`}
                        >
                            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}/page</option>)}
                        </select>
                    </div>
                    <div className="inline-flex items-center gap-2">
                        {total > 0 && (
                            <button
                                onClick={() => openConfirm("filtered")}
                                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5"
                                data-testid={`bulk-delete-${moduleKey}-delete-all-filtered`}
                            >
                                <AlertTriangle className="w-3.5 h-3.5" /> Delete All {total} Filtered
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ---- Confirmation modal ---- */}
            {confirmOpen && (
                <ConfirmDeleteDialog
                    moduleKey={moduleKey}
                    label={meta.label}
                    mode={confirmMode}
                    count={confirmMode === "selected" ? selected.size : total}
                    deleting={deleting}
                    filterPhrase={filterPhrase}
                    setFilterPhrase={setFilterPhrase}
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={performDelete}
                />
            )}
        </div>
    );
}

function FilterField({ label, children }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">{label}</label>
            {children}
        </div>
    );
}

function Cell({ value, type, statusValues }) {
    if (value == null || value === "") return <span className="text-slate-300">—</span>;
    if (type === "datetime") {
        try {
            const d = new Date(value);
            if (isNaN(d.getTime())) return <span>{String(value)}</span>;
            return <span className="text-xs whitespace-nowrap">{d.toLocaleString()}</span>;
        } catch { return <span>{String(value)}</span>; }
    }
    if (type === "status") {
        const colors = {
            new: "bg-blue-100 text-blue-800",
            contacted: "bg-amber-100 text-amber-800",
            in_progress: "bg-indigo-100 text-indigo-800",
            converted: "bg-emerald-100 text-emerald-800",
            lost: "bg-rose-100 text-rose-800",
            under_review: "bg-amber-100 text-amber-800",
            approved: "bg-emerald-100 text-emerald-800",
            rejected: "bg-rose-100 text-rose-800",
        };
        const cls = colors[value] || "bg-slate-100 text-slate-700";
        return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`}>{String(value).replaceAll("_", " ")}</span>;
    }
    if (Array.isArray(value)) {
        return <span className="text-xs">{value.slice(0, 3).join(", ")}{value.length > 3 ? ` +${value.length - 3}` : ""}</span>;
    }
    const s = String(value);
    return <span className="text-xs">{s.length > 80 ? s.slice(0, 80) + "…" : s}</span>;
}

function ConfirmDeleteDialog({ moduleKey, label, mode, count, deleting, filterPhrase, setFilterPhrase, onCancel, onConfirm }) {
    const requirePhrase = mode === "filtered";
    const phraseOk = !requirePhrase || filterPhrase.trim() === "DELETE ALL FILTERED";
    return (
        <div className="fixed inset-0 z-[130] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150" data-testid={`bulk-delete-${moduleKey}-confirm-modal`}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white px-5 py-4 flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider opacity-80">Permanent Action</div>
                        <div className="text-base font-bold">Confirm bulk delete</div>
                    </div>
                </div>
                <div className="p-5 space-y-3 text-sm text-slate-700">
                    <p>
                        Are you sure you want to permanently delete <strong className="text-red-700">{count}</strong> {label.toLowerCase()} record{count === 1 ? "" : "s"}? This action <strong>cannot be undone</strong>.
                    </p>
                    {requirePhrase && (
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
                                Type <code className="px-1 py-0.5 bg-slate-100 rounded">DELETE ALL FILTERED</code> to confirm
                            </label>
                            <input
                                type="text"
                                value={filterPhrase}
                                onChange={(e) => setFilterPhrase(e.target.value)}
                                placeholder="DELETE ALL FILTERED"
                                className="w-full px-3 py-2 rounded-lg border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
                                data-testid={`bulk-delete-${moduleKey}-confirm-phrase-input`}
                                autoFocus
                            />
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                    <button
                        onClick={onCancel}
                        disabled={deleting}
                        className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-50"
                        data-testid={`bulk-delete-${moduleKey}-confirm-cancel`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={deleting || !phraseOk}
                        className="px-3 py-1.5 text-sm font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                        data-testid={`bulk-delete-${moduleKey}-confirm-delete`}
                    >
                        {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Delete Permanently
                    </button>
                </div>
            </div>
        </div>
    );
}
